/**
 * Mirrors the backend types in apps/web/kakehashi/api/src/types.ts.
 * Kept hand-rolled (no codegen) — backend lives in a different package
 * and Phase 1 ships an in-memory store; codegen is overkill here.
 */
export type TaskStatus = "todo" | "doing" | "review" | "done";
export type TaskPriority = "P0" | "P1" | "P2" | "P3";
export type DependencyType = "FS" | "SS" | "FF" | "SF";

export const TASK_STATUSES: readonly TaskStatus[] = [
  "todo",
  "doing",
  "review",
  "done",
] as const;

export const TASK_PRIORITIES: readonly TaskPriority[] = [
  "P0",
  "P1",
  "P2",
  "P3",
] as const;

export const STATUS_LABELS_JA: Record<TaskStatus, string> = {
  todo: "ToDo",
  doing: "Doing",
  review: "Review",
  done: "Done",
};

export const PRIORITY_LABELS_JA: Record<TaskPriority, string> = {
  P0: "P0 — 緊急",
  P1: "P1 — 高",
  P2: "P2 — 中",
  P3: "P3 — 低",
};

export interface TaskExecution {
  kind: "ca_phone" | "ca_case" | "x_reply" | "video_upload";
  source_id: string;
  url: string;
}

export interface Task {
  id: string;
  workspace_id: string;
  room_id: string | null;
  parent_task_id: string | null;
  project_id: string | null;
  title: string;
  description: string;
  execution?: TaskExecution;
  status: TaskStatus;
  priority: TaskPriority;
  due_at: number | null;
  start_at: number | null;
  estimate_min: number | null;
  actual_min: number | null;
  assignee: string | null;
  reporter: string | null;
  tags: string[];
  custom_fields: Record<string, unknown>;
  source_message_id: string | null;
  position: number;
  created_at: number;
  updated_at: number;
  completed_at: number | null;
  archived_at: number | null;
}

export interface TaskDependency {
  predecessor_id: string;
  successor_id: string;
  type: DependencyType;
  lag_min: number;
  created_at: number;
}

export interface TaskComment {
  id: string;
  task_id: string;
  user_id: string;
  body_md: string;
  created_at: number;
  source_id?: string;
  source_kind?: "integration";
}

export interface KanbanColumn {
  status: TaskStatus;
  title: string;
  tasks: Task[];
}

export interface KanbanView {
  columns: KanbanColumn[];
}

export interface GanttBar {
  task: Task;
  start_at: number;
  end_at: number;
  blocked_by: string[];
  blocks: string[];
}

export interface GanttView {
  from: number;
  to: number;
  bars: GanttBar[];
}

export interface CreateTaskInput {
  workspace_id?: string;
  room_id?: string | null;
  parent_task_id?: string | null;
  project_id?: string | null;
  title: string;
  description?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  due_at?: number | null;
  start_at?: number | null;
  estimate_min?: number | null;
  assignee?: string | null;
  reporter?: string | null;
  tags?: string[];
  custom_fields?: Record<string, unknown>;
  source_message_id?: string | null;
}

export interface UpdateTaskInput {
  title?: string;
  description?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  due_at?: number | null;
  start_at?: number | null;
  estimate_min?: number | null;
  actual_min?: number | null;
  assignee?: string | null;
  reporter?: string | null;
  tags?: string[];
  custom_fields?: Record<string, unknown>;
}
